import { SignedIn, SignedOut, useAuth } from '@clerk/clerk-react'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { AppShell } from './components/AppShell'
import { Skeleton } from './components/ui'
import { ApiProvider } from './lib/api-context'
import { SignInPage, SignUpPage } from './routes/AuthPages'
import { ConnectProjectPage } from './routes/ConnectProjectPage'
import { JobPage } from './routes/JobPage'
import { MarketingPage } from './routes/MarketingPage'
import { NotFoundPage } from './routes/NotFoundPage'
import { PackagePickerPage } from './routes/PackagePickerPage'
import { ProjectHubPage } from './routes/ProjectHubPage'
import { ProjectsPage } from './routes/ProjectsPage'
import { ReportPage } from './routes/ReportPage'
import { SettingsPage } from './routes/SettingsPage'

/**
 * `/app/*` needs a session. Clerk is asked first because a signed-in user reloading a deep link
 * must land back on it, not on the projects list.
 */
function RequireAuth({ apiBaseUrl }: { apiBaseUrl: string }) {
  const { isLoaded } = useAuth()
  const location = useLocation()

  if (!isLoaded) {
    return (
      <div className="shell__main">
        <Skeleton rows={4} />
      </div>
    )
  }

  return (
    <>
      <SignedIn>
        <ApiProvider baseUrl={apiBaseUrl}>
          <AppShell />
        </ApiProvider>
      </SignedIn>
      <SignedOut>
        <Navigate
          to={`/sign-in?redirect_url=${encodeURIComponent(location.pathname + location.search)}`}
          replace
        />
      </SignedOut>
    </>
  )
}

export function App({ apiBaseUrl }: { apiBaseUrl: string }) {
  return (
    <Routes>
      <Route path="/" element={<MarketingPage />} />
      <Route path="/sign-in/*" element={<SignInPage />} />
      <Route path="/sign-up/*" element={<SignUpPage />} />

      <Route path="/app" element={<RequireAuth apiBaseUrl={apiBaseUrl} />}>
        <Route index element={<Navigate to="/app/projects" replace />} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/new" element={<ConnectProjectPage />} />
        <Route path="projects/:projectId" element={<ProjectHubPage />} />
        <Route path="projects/:projectId/packages" element={<PackagePickerPage />} />
        <Route path="jobs/:jobId" element={<JobPage />} />
        <Route path="jobs/:jobId/report" element={<ReportPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}
